// Ledger UI primitives — the shared vocabulary of the LOG / PLAN / TRENDS
// surfaces (design handoff `prototype/ledger-ui.jsx`). Everything is inline
// style on the `C` tokens; no radius, no shadows, hairlines only.

import type { CSSProperties, ReactNode } from 'react';
import { C, FONT_DISPLAY, FONT_MONO } from '../../lib/design';

/** 10px uppercase 0.2em label (faint by default). */
export function Lbl({
  children,
  color,
  style,
  className,
}: {
  children: ReactNode;
  color?: string;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        color: color ?? C.faint,
        fontSize: 10,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** 10px 0.1em sub-label (dim by default). */
export function Sub({
  children,
  color,
  style,
  className,
}: {
  children: ReactNode;
  color?: string;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{ color: color ?? C.dim, fontSize: 10, letterSpacing: '0.1em', ...style }}
    >
      {children}
    </div>
  );
}

export function Btn({
  children,
  primary,
  danger,
  ghost,
  onClick,
  disabled,
  style,
  title,
  type = 'button',
  ariaLabel,
}: {
  children: ReactNode;
  primary?: boolean;
  danger?: boolean;
  ghost?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  style?: CSSProperties;
  title?: string;
  type?: 'button' | 'submit';
  ariaLabel?: string;
}) {
  const base: CSSProperties = {
    fontFamily: FONT_MONO,
    fontSize: 10,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    padding: '8px 12px',
    cursor: disabled ? 'default' : 'pointer',
    border: `1px solid ${C.line}`,
    background: 'transparent',
    color: C.dim,
    opacity: disabled ? 0.4 : 1,
    whiteSpace: 'nowrap',
  };
  if (primary) {
    Object.assign(base, {
      background: C.signal,
      color: C.bg,
      border: `1px solid ${C.signal}`,
      fontWeight: 700,
    });
  }
  if (danger) Object.assign(base, { color: C.red, border: `1px solid ${C.red}` });
  if (ghost) Object.assign(base, { border: 0, padding: '4px 0', color: C.signal });
  return (
    <button
      type={type}
      title={title}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      style={{ ...base, ...style }}
    >
      {children}
    </button>
  );
}

export type SegOption<T extends string> = T | [T, string];

/** Segmented control. Options are `value` or `[value, label]` tuples. */
export function Seg<T extends string>({
  options,
  value,
  onChange,
  style,
  wrap,
  ariaLabel,
}: {
  options: ReadonlyArray<SegOption<T>>;
  /** `undefined` leaves every segment unselected (nothing chosen yet). */
  value: T | undefined;
  onChange: (v: T) => void;
  style?: CSSProperties;
  /** Allow the segments to wrap onto multiple rows (many options). */
  wrap?: boolean;
  ariaLabel?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        flexWrap: wrap ? 'wrap' : undefined,
        border: `1px solid ${C.line}`,
        ...style,
      }}
    >
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o[0];
        const l = typeof o === 'string' ? o : o[1];
        const on = v === value;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(v)}
            style={{
              flex: 1,
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: '0.1em',
              padding: '6px 8px',
              background: on ? C.signal : 'transparent',
              color: on ? C.bg : C.dim,
              border: 0,
              borderLeft: `1px solid ${C.line}`,
              cursor: 'pointer',
              fontWeight: on ? 700 : 400,
              whiteSpace: 'nowrap',
            }}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

/** Text-input chrome shared by every ledger field. */
export const INPUT_STYLE: CSSProperties = {
  width: '100%',
  background: C.bg,
  color: C.text,
  border: `1px solid ${C.line}`,
  padding: '8px 10px',
  fontFamily: FONT_MONO,
  fontSize: 13,
  outline: 'none',
};

/** Labelled numeric field. */
export function Field({
  label,
  value,
  onChange,
  width,
  suffix,
  step,
  min,
  autoFocus,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  width?: number | string;
  suffix?: string;
  step?: number;
  min?: number;
  autoFocus?: boolean;
}) {
  return (
    <label style={{ display: 'block', minWidth: 0 }}>
      <Lbl style={{ marginBottom: 4 }}>{label}</Lbl>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <input
          className="num"
          type="number"
          step={step ?? 1}
          min={min}
          autoFocus={autoFocus}
          aria-label={label}
          value={Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{ ...INPUT_STYLE, width: width ?? '100%' }}
        />
        {suffix && <span style={{ color: C.faint, fontSize: 10 }}>{suffix}</span>}
      </div>
    </label>
  );
}

/** Solid-bordered content panel with an optional hairline title row. */
export function Panel({
  title,
  right,
  children,
  style,
  className,
}: {
  title?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{ background: C.panel, border: `1px solid ${C.line}`, minWidth: 0, ...style }}
    >
      {title != null && (
        <div
          style={{
            padding: '10px 14px',
            borderBottom: `1px solid ${C.line}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 12,
          }}
        >
          <Lbl>{title}</Lbl>
          {right != null && (typeof right === 'string' ? <Sub>{right}</Sub> : right)}
        </div>
      )}
      {children}
    </div>
  );
}

/** Thin progress bar (3px). */
export function Bar({
  value,
  max,
  color,
  height,
}: {
  value: number;
  max: number;
  color?: string;
  height?: number;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, Math.round((value / max) * 100))) : 0;
  return (
    <div style={{ height: height ?? 3, background: C.line, position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          width: `${pct}%`,
          background: color ?? C.signal,
        }}
      />
    </div>
  );
}

/** Minimal sparkline with an optional dashed baseline. Renders nothing for
 *  fewer than two points. */
export function Spark({
  values,
  color,
  w,
  h,
  baseline,
}: {
  values: number[];
  color?: string;
  w?: number;
  h?: number;
  baseline?: number | null;
}) {
  const W = w ?? 140;
  const H = h ?? 36;
  const pad = 2;
  if (values.length < 2) {
    return <div style={{ height: H, borderBottom: `1px dashed ${C.line}` }} />;
  }
  const min = Math.min(...values, baseline ?? Infinity) - 0.3;
  const max = Math.max(...values, baseline ?? -Infinity) + 0.3;
  const xs = (W - pad * 2) / (values.length - 1);
  const y = (v: number) => H - pad - ((v - min) / (max - min || 1)) * (H - pad * 2);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${pad + i * xs} ${y(v)}`).join(' ');
  const last = values[values.length - 1]!;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {baseline != null && (
        <line
          x1={pad}
          x2={W - pad}
          y1={y(baseline)}
          y2={y(baseline)}
          stroke={C.faint}
          strokeDasharray="3 3"
        />
      )}
      <path d={d} stroke={color ?? C.signal} strokeWidth="1.5" fill="none" />
      <rect
        x={pad + (values.length - 1) * xs - 1.5}
        y={y(last) - 1.5}
        width="3"
        height="3"
        fill={C.text}
      />
    </svg>
  );
}

/** Single-line ellipsis wrapper. */
export function Ell({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Dashed callout block, e.g. a coach note. */
export function Note({
  label,
  children,
  color,
  style,
}: {
  label: ReactNode;
  children: ReactNode;
  color?: string;
  style?: CSSProperties;
}) {
  return (
    <div style={{ border: `1px dashed ${C.line}`, padding: '10px 12px', ...style }}>
      <Lbl color={color ?? C.signal} style={{ marginBottom: 6 }}>
        {label}
      </Lbl>
      <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

/** Dashed red error strip. */
export function ErrorStrip({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      style={{
        border: `1px dashed ${C.red}`,
        color: C.red,
        padding: '8px 10px',
        fontSize: 10,
        letterSpacing: '0.05em',
        lineHeight: 1.5,
      }}
    >
      {message}
    </div>
  );
}

export const DISPLAY = FONT_DISPLAY;
export const MONO = FONT_MONO;
